<script>
  import rules from '@/helpers/validation-rules';
  import OutlinedInput from '@/components/form/OutlinedInput.vue';
  import OutlinedSelect from '@/components/form/OutlinedSelect.vue';
  import WhiteDialog from '@/components/dialogs/WhiteDialog.vue';
  import TermsAndConditionsText from '@/components/sections/TermsAndConditionsText.vue';
  import SignUpPrivacyPolicy from '@/components/sections/SignUpPrivacyPolicy.vue';
  import ReCaptcha from '@/components/form/ReCaptcha.vue';
  import { debounce } from 'lodash';
  import { HasTouch } from '@/mixins/hasTouch';

  export default {
    name: 'SignupPersonalInfo',
    components: {
      ReCaptcha,
      SignUpPrivacyPolicy,
      TermsAndConditionsText,
      WhiteDialog,
      OutlinedSelect,
      OutlinedInput,
    },
    mixins: [HasTouch],
    props: {
      model: {
        type: Object,
        required: true,
      },
      countryList: {
        type: Array,
        required: true,
      },
      statesList: {
        type: Array,
        required: true,
      },
      monthList: {
        type: Array,
        required: true,
      },
    },

    data: () => ({
      rules,
      tncDialog: false,
      privacyDialog: false,
      loading: false,
      tnc: false,
      usernameAvailable: null,
      isUsernameValid: false,
      loadings: {
        username: false,
      },
    }),

    computed: {
      isSocialSignUp() {
        return this.$store.state.isSocialSignUp;
      },

      usernameInputRules() {
        return [rules.required(), rules.username(), rules.maxLength(16), rules.minLength(4)];
      },
      usernameRule() {
        return () =>
          this.usernameAvailable === true || this.usernameAvailable === null || 'This username is already taken';
      },

      userInfo() {
        return this.$store.getters.getuser;
      },

      countries() {
        return this.countryList;
      },
      states() {
        return this.statesList;
      },
      day() {
        return this.model.birthdate.day;
      },
      month() {
        return this.model.birthdate.month;
      },
      year() {
        return this.model.birthdate.year;
      },
    },

    watch: {
      'model.birthdate.month': async function () {
        await this.$nextTick();
        this.revalidateFields();
      },
      'model.birthdate.year': async function () {
        await this.$nextTick();
        this.revalidateFields();
      },
      'model.username': {
        immediate: true,
        handler(value) {
          const messages = this.usernameInputRules.map((rule) => rule(value)).filter((message) => message !== true);
          this.isUsernameValid = messages.length === 0;
        },
      },
      usernameAvailable() {
        this.$refs.username?.$refs?.input?.validate();
      },
    },

    methods: {
      checkIfUsernameAvailable: debounce(async function (username) {
        if (!this.isUsernameValid) {
          this.usernameAvailable = null;
          return;
        }

        this.loadings.username = true;

        try {
          const { data } = await this.$store.dispatch('checkUsernameAvailability', username);
          this.usernameAvailable = !data.hasError;
        } catch (e) {
          console.error(e);
        }

        this.loadings.username = false;
      }, 300),

      getMaxDaysOfMonth(year, month) {
        return new Date(year, month, 0).getDate();
      },

      revalidateFields() {
        if (this.$refs.dayField) {
          this.$refs.dayField.$refs.input.validate();
        }
        if (this.$refs.monthField) {
          this.$refs.monthField.$refs.input.validate();
        }
        if (this.$refs.yearField) {
          this.$refs.yearField.$refs.input.validate();
        }
      },

      openTNC() {
        this.tncDialog = true;
      },

      openPrivacy() {
        this.privacyDialog = true;
      },

      async handleRecaptchaClick() {
        if (this.$refs.form.validate() && !this.loadings.username && !this.loadings.email) {
          await this.$refs.recaptcha.execute();
        }
      },

      async submit(token) {
        try {
          if (this.$refs.form.validate() && !this.loadings.username) {
            this.register(token);
          }
        } catch (e) {
          console.error(e);
        }
      },

      async register(token) {
        this.loading = true;

        const newUser = {
          displayName: this.model.username,
          email: this.model.email,
          password: this.model.password,
          checkAds: this.model.marketing,
          check18: this.model.terms,
          googleToken: token,
          country: this.model.country || 'USA',
          state: this.model.state,
          dateofbirth: `${this.model.birthdate.year}-${this.model.birthdate.month}-${this.model.birthdate.day}`,
        };

        if (this.isSocialSignUp) {
          await this.$store.dispatch('signUserUp', { ...newUser, viaProvider: true });
        } else {
          await this.$store.dispatch('signUserUp', newUser);
        }

        this.loading = false;
      },
    },
  };
</script>

<template>
  <v-form ref="form" class="d-flex flex-column mt-6" lazy-validation>
    <div v-if="isSocialSignUp" class="d-flex gap-10">
      <outlined-input
        ref="username"
        v-model="model.username"
        :rules="[...usernameInputRules, usernameRule]"
        class="w-full"
        data-test-id="username-input"
        label="Username"
        @input="checkIfUsernameAvailable(model.username)"
      >
        <template #append>
          <div v-if="isUsernameValid">
            <v-progress-circular v-if="loadings.username" color="white" indeterminate size="25" width="2" />
            <v-icon v-else-if="usernameAvailable" class="green--text">mdi-check</v-icon>
          </div>
        </template>
      </outlined-input>
    </div>

    <div class="d-flex gap-10">
      <outlined-select
        ref="monthField"
        v-model="model.birthdate.month"
        :items="monthList"
        :rules="[rules.required()]"
        data-test-id="select-month"
        label="Date of Birth"
        outlined
        placeholder="Month"
        @change="revalidateFields"
      />
      <outlined-input
        ref="dayField"
        v-model="model.birthdate.day"
        v-mask="'##'"
        :class="$vuetify.breakpoint.xs ? 'left-side-error' : ''"
        :rules="[rules.max(getMaxDaysOfMonth(year, month)), rules.min(1), rules.onlyInteger()]"
        data-test-id="select-day"
        hide-required-star
        outlined
        persistent-hint
        persistent-placeholder
        placeholder="D D"
        @input="revalidateFields"
      />
      <outlined-input
        ref="yearField"
        v-model="model.birthdate.year"
        v-mask="'####'"
        :rules="[
          rules.max(new Date().getFullYear()),
          rules.min(1900),
          rules.ageValidation(
            () => model.birthdate.day,
            () => model.birthdate.month,
            () => model.birthdate.year,
          ),
          rules.onlyInteger(),
        ]"
        data-test-id="select-year"
        hide-required-star
        outlined
        persistent-hint
        persistent-placeholder
        placeholder="Y Y Y Y"
        @input="revalidateFields"
      />
    </div>

    <div class="d-flex gap-10">
      <outlined-input
        v-model="model.country"
        data-test-id="country-input"
        disabled
        label="Country & State"
        persistent-hint
      />
      <outlined-select
        v-model="model.state"
        :items="states"
        :rules="[rules.required()]"
        class="w-full"
        data-test-id="select-state"
        hide-required-star
        item-text="name"
        item-value="code"
        outlined
        placeholder="State"
        return-object
        show-focused-placeholder
      />
    </div>

    <v-checkbox
      v-model="tnc"
      :rules="[rules.required()]"
      class="mt-0 white-checkbox"
      color="gold"
      data-test-id="terms-and-privacy-checkbox"
    >
      <template #label>
        <span class="white--text">
          I agree to the
          <span class="text-underline" data-test-id="open-tnc" @click.stop.prevent="openTNC()">Terms & Conditions</span>
          &
          <span class="text-underline" data-test-id="open-privacy" @click.stop.prevent="openPrivacy()">
            Privacy Policy
          </span>
        </span>
      </template>
    </v-checkbox>

    <v-btn :loading="loading" class="golden-button mt-4" data-test-id="next-step" @click.prevent="handleRecaptchaClick">
      <span>Next Step</span>
      <v-icon class="mt-1 font-size-30">mdi-arrow-right-thin</v-icon>
    </v-btn>

    <re-captcha ref="recaptcha" @verify="submit" />

    <white-dialog v-model="tncDialog">
      <template #content>
        <terms-and-conditions-text dark />
      </template>
    </white-dialog>

    <white-dialog v-model="privacyDialog" content-class="privacy-policy-dialog">
      <template #content>
        <sign-up-privacy-policy dark />
      </template>
    </white-dialog>
  </v-form>
</template>
<style lang="scss" scoped>
  .gap-10 {
    gap: 10px;
  }

  .white-checkbox::v-deep {
    .v-icon.mdi-checkbox-blank-outline {
      color: white;
    }
  }

  .text-underline {
    text-decoration: underline;
    cursor: pointer;
    color: var(--v-gold-base);
  }

  .v-list::v-deep {
    background: #191919 !important;

    .v-list-item {
      color: white !important;

      .flagImage {
        display: flex;
        align-items: center;

        svg {
          border-radius: 2px;
          width: 25px;
          margin-right: 5px;
        }
      }
    }
  }

  .formText::v-deep {
    .v-select__slot {
      .v-select__selections {
        color: white;
      }

      .flagImage {
        margin-top: 2px;
        display: flex;
        align-items: center;

        svg {
          border-radius: 2px;
          width: 25px;
          margin-right: 5px;
        }
      }
    }
  }

  @media screen and (max-width: 600px) {
    .privacy-policy-dialog {
      .v-card__text {
        padding-inline: 0;
      }

      .container {
        padding: 12px 0;
      }
    }
  }
</style>

<style lang="scss">
  body.hasTouch {
    .v-input--selection-controls__input:hover .v-input--selection-controls__ripple:before {
      display: none;
    }
  }
  @media screen and (max-width: 600px) {
    .privacy-policy-dialog {
      .v-card__text {
        padding-inline: 0;
      }

      .container {
        padding: 12px 0;
      }
    }
  }
</style>
